import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
} from "react-router-dom";
import {
  apiService,
  BotStatus,
  Guild,
  Member,
  MusicPlayerState,
  Command,
  LogEntry,
  BotInfo,
} from "./services/api";
import { Dashboard } from "./components/Dashboard";
import { Members } from "./components/Members";
import { MusicPlayer } from "./components/MusicPlayer";
import { Commands } from "./components/Commands";
import { Logs } from "./components/Logs";
import { Settings } from "./components/Settings";
import { Login } from "./components/Login";

function AppContent() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null);
  const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
  const [guilds, setGuilds] = useState<Guild[]>([]);
  const [selectedGuild, setSelectedGuild] = useState<string>("");
  const [members, setMembers] = useState<Member[]>([]);
  const [musicStatus, setMusicStatus] = useState<MusicPlayerState | null>(null);
  const [commands, setCommands] = useState<{ slash: Command[]; prefix: Command[] }>({
    slash: [],
    prefix: [],
  });
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Check authentication on startup
  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = await apiService.verifyAuth();
      setIsAuthenticated(isAuth);
    };

    checkAuth();

    const handleLogout = () => {
      setIsAuthenticated(false);
    };
    window.addEventListener("auth_logout", handleLogout);
    return () => window.removeEventListener("auth_logout", handleLogout);
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusData, botInfoData, guildsData, musicData, commandsData, logsData] =
        await Promise.all([
          apiService.getBotStatus().catch(() => null),
          apiService.getBotInfo().catch(() => null),
          apiService.getGuilds().catch(() => []),
          apiService.getMusicStatus().catch(() => null),
          apiService.getCommands().catch(() => ({ slash: [], prefix: [] })),
          apiService.getLogs().catch(() => []),
        ]);

      if (statusData) setBotStatus(statusData);
      if (botInfoData) setBotInfo(botInfoData);
      if (guildsData) {
        setGuilds(guildsData);
        if (guildsData.length > 0 && !selectedGuild) {
          setSelectedGuild(guildsData[0].id);
        }
      }
      if (musicData) setMusicStatus(musicData);
      if (commandsData) setCommands(commandsData);
      if (logsData) setLogs(logsData);
    } catch (error) {
      console.error("Failed to load initial data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedGuild]);

  useEffect(() => {
    if (isAuthenticated) {
      loadInitialData();
    }
  }, [isAuthenticated, loadInitialData]);

  useEffect(() => {
    if (isAuthenticated && selectedGuild) {
      apiService
        .getGuildMembers(selectedGuild)
        .then((data) => setMembers(data))
        .catch(() => setMembers([]));
    }
  }, [isAuthenticated, selectedGuild]);

  const refreshMusicStatus = async () => {
    try {
      const musicData = await apiService.getMusicStatus();
      setMusicStatus(musicData);
    } catch (error) {
      console.error("Failed to refresh music status:", error);
    }
  };

  const handleLogout = () => {
    apiService.logout();
    setIsAuthenticated(false);
  };

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === "/" || path === "/dashboard") return "dashboard";
    if (path === "/members") return "members";
    if (path === "/music") return "music";
    if (path === "/commands") return "commands";
    if (path === "/logs") return "logs";
    if (path === "/settings") return "settings";
    return "dashboard";
  };

  // Checking auth status
  if (isAuthenticated === null) {
    return (
      <div className="login-wrapper">
        <div className="loading">Verifica autenticazione in corso...</div>
      </div>
    );
  }

  // Not logged in -> Show Login Component
  if (!isAuthenticated) {
    return (
      <Login
        onLoginSuccess={() => {
          setIsAuthenticated(true);
        }}
      />
    );
  }

  return (
    <div className="app-layout">
      {/* Top Navbar */}
      <header className="top-navbar">
        <div className="brand-section">
          <div className="brand-logo-wrap">
            <img
              src={botInfo?.botAvatar || "https://cdn.discordapp.com/embed/avatars/0.png"}
              alt="Bot"
              className="brand-avatar"
            />
            <span className={`brand-status-dot ${botStatus?.online ? "online" : "offline"}`}></span>
          </div>
          <div className="brand-titles">
            <span className="brand-name">{botInfo?.botName || "VortexBot"}</span>
            <span className="brand-version">v2.0 Dashboard</span>
          </div>
        </div>

        {/* Global Navigation Tabs */}
        <nav className="nav-tabs">
          <Link
            to="/dashboard"
            className={`nav-tab ${getActiveTab() === "dashboard" ? "active" : ""}`}
          >
            📊 Dashboard
          </Link>
          <Link
            to="/music"
            className={`nav-tab ${getActiveTab() === "music" ? "active" : ""}`}
          >
            🎵 Musica
          </Link>
          <Link
            to="/members"
            className={`nav-tab ${getActiveTab() === "members" ? "active" : ""}`}
          >
            👥 Membri
          </Link>
          <Link
            to="/commands"
            className={`nav-tab ${getActiveTab() === "commands" ? "active" : ""}`}
          >
            ⌨️ Comandi
          </Link>
          <Link
            to="/logs"
            className={`nav-tab ${getActiveTab() === "logs" ? "active" : ""}`}
          >
            📜 Log Errori
          </Link>
          <Link
            to="/settings"
            className={`nav-tab ${getActiveTab() === "settings" ? "active" : ""}`}
          >
            ⚙️ Impostazioni
          </Link>
        </nav>

        {/* Header Actions */}
        <div className="header-actions">
          <div className="header-badge">
            <span className="ping-text">{botStatus?.ping ? `${botStatus.ping}ms` : "OK"}</span>
          </div>
          <button onClick={handleLogout} className="logout-btn" title="Disconnettiti dalla Dashboard">
            Esci 🚪
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="main-content-container">
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                botStatus={botStatus}
                guilds={guilds}
                loading={loading}
              />
            }
          />
          <Route
            path="/dashboard"
            element={
              <Dashboard
                botStatus={botStatus}
                guilds={guilds}
                loading={loading}
              />
            }
          />
          <Route
            path="/members"
            element={
              <Members
                guilds={guilds}
                selectedGuild={selectedGuild}
                setSelectedGuild={setSelectedGuild}
                members={members}
                loading={loading}
              />
            }
          />
          <Route
            path="/music"
            element={
              <MusicPlayer
                guilds={guilds}
                selectedGuild={selectedGuild}
                setSelectedGuild={setSelectedGuild}
                musicStatus={musicStatus}
                onRefreshMusicStatus={refreshMusicStatus}
              />
            }
          />
          <Route
            path="/commands"
            element={<Commands commands={commands} loading={loading} />}
          />
          <Route
            path="/logs"
            element={<Logs logs={logs} loading={loading} />}
          />
          <Route
            path="/settings"
            element={
              <Settings
                botStatus={botStatus}
                loading={loading}
                selectedGuild={selectedGuild}
                guilds={guilds}
                onSelectGuild={setSelectedGuild}
              />
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
